/**
 * HeadingProvider — abstract interface
 *
 *   subscribe(callback) -> () => unsubscribe
 *   callback({ headingDeg, accuracyDeg, source })
 *
 *   source: "compass" (true magnetic heading, iOS Safari)
 *         | "orientation" (DeviceOrientation alpha)
 *         | "gps" (course over ground)
 *
 *   accuracyDeg: 0 = perfect, higher = less confident. We use this on the
 *   render side to fade the directional cone in/out so a poor compass
 *   doesn't spin the user's puck like a propeller.
 */

export class HeadingProvider {
  // eslint-disable-next-line no-unused-vars
  subscribe(_cb) { throw new Error("HeadingProvider.subscribe not implemented"); }
  destroy() {}
  // eslint-disable-next-line class-methods-use-this
  async requestPermission() { return "granted"; }
}

/**
 * Web implementation. iOS Safari only fires `deviceorientation` AFTER an
 * explicit user-gesture-triggered `DeviceOrientationEvent.requestPermission()`
 * grant. Android Chrome fires it as soon as you attach the listener.
 */
export class WebHeadingProvider extends HeadingProvider {
  constructor() {
    super();
    this._handler = null;
    this._cb = null;
  }

  async requestPermission() {
    // iOS 13+ requires explicit permission. The call must originate inside
    // a user-gesture handler — we proxy through the locate-me button.
    const needsRequest = typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function";
    if (!needsRequest) return "granted";
    try {
      const state = await DeviceOrientationEvent.requestPermission();
      return state; // "granted" | "denied"
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[HeadingProvider] requestPermission failed:", e?.message);
      return "denied";
    }
  }

  subscribe(cb) {
    this._cb = cb;
    let lastEmit = 0;
    const handler = (event) => {
      // Coalesce DeviceOrientation events to ~15 Hz. The raw stream runs
      // at 60 Hz which floods React + every consumer (map.setHeading,
      // marker rotation, etc.) and is the dominant cause of map-gesture
      // stutter. 15 Hz is still visually smooth for the cone.
      const now = Date.now();
      if (now - lastEmit < 66) return;
      lastEmit = now;
      // iOS Safari exposes a true magnetic heading via webkitCompassHeading.
      // Lower numeric webkitCompassAccuracy is BETTER (in degrees).
      let headingDeg = null;
      let accuracyDeg = 30;
      let source = "orientation";
      if (typeof event.webkitCompassHeading === "number") {
        headingDeg = event.webkitCompassHeading; // 0..360, north=0, CW
        accuracyDeg = typeof event.webkitCompassAccuracy === "number" ? event.webkitCompassAccuracy : 15;
        source = "compass";
      } else if (typeof event.alpha === "number") {
        // Android Chrome: alpha is rotation around Z, 0..360, but it's NOT
        // referenced to magnetic north by default. The `deviceorientation
        // absolute` event (if supported) IS true north. We treat alpha as
        // best-effort with a wider accuracy band so the cone fades.
        // Note: alpha decreases as you rotate clockwise, so we invert.
        headingDeg = (360 - event.alpha) % 360;
        accuracyDeg = event.absolute ? 15 : 30;
        source = event.absolute ? "compass" : "orientation";
      }
      if (headingDeg === null || Number.isNaN(headingDeg)) return;
      cb({ headingDeg, accuracyDeg, source });
    };
    this._handler = handler;
    // Prefer the absolute variant when the browser supports it (truer north).
    if (typeof window !== "undefined" && "ondeviceorientationabsolute" in window) {
      window.addEventListener("deviceorientationabsolute", handler, true);
    } else if (typeof window !== "undefined") {
      window.addEventListener("deviceorientation", handler, true);
    }
    return () => this.destroy();
  }

  destroy() {
    if (!this._handler) return;
    if (typeof window !== "undefined" && "ondeviceorientationabsolute" in window) {
      window.removeEventListener("deviceorientationabsolute", this._handler, true);
    } else if (typeof window !== "undefined") {
      window.removeEventListener("deviceorientation", this._handler, true);
    }
    this._handler = null;
  }
}

/**
 * Circular low-pass filter for heading. Heading is an angle on S^1 so we
 * smooth via the X/Y unit-vector trick — averaging in radians directly
 * breaks across the 359°→0° seam.
 */
export class HeadingSmoother {
  constructor({ alpha = 0.25 } = {}) {
    this.a = alpha;
    this.x = null; this.y = null;
  }

  push(deg) {
    const r = (deg * Math.PI) / 180;
    const cx = Math.cos(r);
    const cy = Math.sin(r);
    if (this.x === null) { this.x = cx; this.y = cy; return deg; }
    this.x = this.x * (1 - this.a) + cx * this.a;
    this.y = this.y * (1 - this.a) + cy * this.a;
    const smoothed = (Math.atan2(this.y, this.x) * 180) / Math.PI;
    return (smoothed + 360) % 360;
  }

  reset() { this.x = null; this.y = null; }
}
